import React from 'react';
import { HStack, Box, Text } from '@cad-arc';
import { UseFormReturn } from 'react-hook-form';
import { BoloPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { ConditionalWrapper } from '../../../common/components/conditional-wrapper';
import { NoMembersIndicator } from '../../../event-panel/tabs/components/no-members-indicator';
import { EntityPanelFormGroup } from '../../../panels/entity-panels/entity-panel-components';
import { FormInputButton } from '../../../forms/integrated-form-components/form-button';
import { useBootstrapState } from '../../../bootstrap/bootstrap-provider';
import { useTrackedFieldArray } from '../../../forms/use-tracked-field-array';
import { TabCard } from '../../../event-panel/tabs/components/tab-card';
import { EntityPanelFormVehiclesFieldset } from '../../../panels/entity-panels/forms/vehicles/entity-panel-vehicles-fieldset';
import { getDefaultVehicle } from '../../../event-panel/event-panel-form/involved-person-vehicle-fields/vehicles/event-panel-vehicle-helpers';
import { BoloPanelFormIsPrimaryCheck } from './bolo-panel-form-is-primary-check';

export type BoloPanelFormVehiclesFieldsProps = {
    form: UseFormReturn<BoloPanelFormType>;
    eventPanelOptions: EventPanelOptions;
    showTitle?: boolean;
    noMembersText?: string;
    displayAsCards?: boolean;
    hideDexFields?: boolean;
    isReadOnly?: boolean;
    isClearedBolo?: boolean;
    allowMembersControl?: boolean;
    isNewBolo: boolean;
};

export function BoloPanelFormVehiclesFields(
    props: BoloPanelFormVehiclesFieldsProps
): React.ReactElement {
    const boloForm = props.form;
    const eventPanelConfiguration = useBootstrapState().eventPanelConfiguration;
    const { fields, append, remove } = useTrackedFieldArray({
        control: boloForm.control,
        name: 'involvedVehicles',
        keyName: 'rhfKey',
    });

    const addVehiclesButton =
        props.allowMembersControl && !props.isClearedBolo ? (
            <FormInputButton
                size="md"
                variant="ghost"
                leadingVisual="Add"
                data-testid="add-vehicle-button"
                onClick={() =>
                    append(
                        getDefaultVehicle({
                            eventPanelConfiguration,
                        })
                    )
                }
            >
                Add Vehicle
            </FormInputButton>
        ) : undefined;

    return (
        <EntityPanelFormGroup data-testid="vehicle-form-section">
            {props.showTitle && fields.length > 0 ? (
                <Text variant="headingXs">Vehicles</Text>
            ) : undefined}
            {fields.length === 0 && props.noMembersText ? (
                <NoMembersIndicator
                    text={props.noMembersText}
                    icon="Vehicle"
                    action={addVehiclesButton}
                    value={fields}
                />
            ) : (
                <>
                    {fields.map((field, index) => (
                        <ConditionalWrapper
                            key={field.rhfKey}
                            condition={props.displayAsCards}
                            wrapper={(children) => <TabCard>{children}</TabCard>}
                        >
                            <>
                                <HStack>
                                    <BoloPanelFormIsPrimaryCheck
                                        form={props.form}
                                        index={index}
                                        basePath="involvedVehicles"
                                    />
                                </HStack>
                                <EntityPanelFormVehiclesFieldset
                                    index={index}
                                    remove={remove}
                                    baseKey="involvedVehicles"
                                    entityPanelOptions={props.eventPanelOptions}
                                    allowMembersControl={props.allowMembersControl}
                                    displayAsCards={props.displayAsCards}
                                    isReadOnly={props.isReadOnly}
                                    isNewEntity={props.isNewBolo}
                                />
                            </>
                        </ConditionalWrapper>
                    ))}

                    <Box>{addVehiclesButton}</Box>
                </>
            )}
        </EntityPanelFormGroup>
    );
}
